import { useCallback, useEffect, useMemo, useState } from "react";
import { useDebouncer } from "../lib/utils";
import { defaultFilters, type Filters } from "../lib/consts";
import { useLocation, useSearchParams } from "react-router-dom";

const FILTER_PARAM_KEYS: (keyof Filters)[] = ['location', 'price_range', 'min_bedrooms', 'dateRange', 'sort', 'keywords'];

export default function useFilters(suspendUrlSync = false): [Filters, string, (filters: Filters) => void] {
    /** Get the filters from the URL, or use defaults */
    const [, setSearchParams] = useSearchParams();
    const routerLocation = useLocation();
    const searchParams = useMemo(() => new URLSearchParams(routerLocation.search), [routerLocation.search]);
    const getFilters = useCallback(() => {
        const urlFilters = Object.fromEntries(
            FILTER_PARAM_KEYS
                .map(key => [key, searchParams.get(key)])
                .filter(([, value]) => value !== null)
        );
        return { ...defaultFilters, ...urlFilters } as Filters;
    }, [searchParams]);

    const [filters, setFilters] = useState<Filters>(getFilters);
    const { location, price_range, min_bedrooms, dateRange, sort } = filters;
    const [debouncedKeywords, setDebouncedKeyword] = useState(filters.keywords);
    useDebouncer(setDebouncedKeyword, filters.keywords, value => value.trim().toLowerCase());

    /** Update the filters when the URL changes */
    useEffect(() => setFilters(getFilters()), [getFilters]);

    /** Update the URL when the filters change */
    useEffect(() => {
        if (suspendUrlSync) return;

        const filtersForUrl = { location, price_range, min_bedrooms, dateRange, sort, keywords: debouncedKeywords };

        setSearchParams(prev => {
            const params = new URLSearchParams(prev);
            for (const key of FILTER_PARAM_KEYS) params.delete(key);
            for (const [k, v] of Object.entries(filtersForUrl)) {
                if (v !== '' && v !== defaultFilters[k as keyof Filters]) params.set(k, String(v));
            }
            return params;
        }, { replace: true });
    }, [dateRange, debouncedKeywords, location, min_bedrooms, price_range, setSearchParams, sort, suspendUrlSync]);

    return [filters, debouncedKeywords, setFilters];
}
